import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch } from 'app/store';
import { IReferralHistoryItemModel, IReferralHistoryParams, IReferralSummaryModel } from 'entities/Referral/Referral.models';
import { referralTransport } from 'entities/Referral/Referral.transport';

export interface IState {
  summary: IReferralSummaryModel | null;
  summaryLoading: boolean;

  history: IReferralHistoryItemModel[];
  historyCount: number;
  historyLoading: boolean;
}

const initialState: IState = {
  summary: null,
  summaryLoading: false,

  history: [],
  historyCount: 0,
  historyLoading: false,
};

export const referralSlice = createSlice({
  name: 'referral',
  initialState,
  reducers: {
    setReferralSummary(state, action: PayloadAction<IReferralSummaryModel | null>) {
      state.summary = action.payload;
    },
    setReferralSummaryLoading(state, action: PayloadAction<boolean>) {
      state.summaryLoading = action.payload;
    },
    setReferralHistory(
      state,
      action: PayloadAction<{ data: IReferralHistoryItemModel[]; count?: number; append: boolean; offset: number }>,
    ) {
      const { data, count, append, offset } = action.payload;

      // Staleness guard: an append requested for a different offset (a late response arriving
      // after the list was cleared/refetched on remount) must not be glued onto the fresh list.
      if (append && offset !== state.history.length) {
        return;
      }

      state.history = append ? [...state.history, ...data] : data;
      state.historyCount = count ?? state.history.length;
    },
    setReferralHistoryLoading(state, action: PayloadAction<boolean>) {
      state.historyLoading = action.payload;
    },
    clearReferralHistory(state) {
      state.history = [];
      state.historyCount = 0;
    },
  },
});

export const {
  setReferralSummary,
  setReferralSummaryLoading,
  setReferralHistory,
  setReferralHistoryLoading,
  clearReferralHistory,
} = referralSlice.actions;

export default referralSlice.reducer;

export const getReferralSummary = () => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setReferralSummaryLoading(true));

    try {
      const summary = await referralTransport.getReferralSummary();
      dispatch(setReferralSummary(summary));
    } catch (error) {
      console.log('getReferralSummary error', error);
    } finally {
      dispatch(setReferralSummaryLoading(false));
    }
  }

  return thunk;
};

export const getReferralHistory = (params: IReferralHistoryParams = {}, append = false) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setReferralHistoryLoading(true));

    try {
      const collection = await referralTransport.getReferralHistory(params);
      dispatch(
        setReferralHistory({
          data: collection.data,
          count: collection.meta?.count,
          append,
          offset: params.offset ?? 0,
        }),
      );
    } catch (error) {
      console.log('getReferralHistory error', error);
    } finally {
      dispatch(setReferralHistoryLoading(false));
    }
  }

  return thunk;
};

export const markReferralRewardSeen = () => {
  async function thunk() {
    try {
      await referralTransport.markReferralRewardSeen();
    } catch (error) {
      console.log('markReferralRewardSeen error', error);
    }
  }

  return thunk;
};
